import { usePageTitle } from '../hooks/usePageTitle'
import { useApplications } from '../hooks/useApplications'
import { useUserStats } from '../hooks/useUserStats'
import {
  VStack,
  Grid,
  Card,
  Heading,
  Text,
  DonutChart,
  BarChart,
  StatCard,
  Animate
} from 'wss3-forge'
import { People20Regular, ArrowTrending20Regular } from '@fluentui/react-icons'

export function Statistics() {
  usePageTitle('Statistics')
  const { applications } = useApplications()
  const { globalStats, appStats } = useUserStats()

  const activeApps = applications.filter(app => app.is_active).length
  const inactiveApps = applications.length - activeApps

  const providerCounts = applications.reduce((acc, app) => {
    (app.allowed_providers || ['email']).forEach(provider => {
      acc[provider] = (acc[provider] || 0) + 1
    })
    return acc
  }, {} as Record<string, number>)

  const donutData = [
    { label: 'Actives', value: activeApps, color: 'var(--success)' },
    { label: 'Inactives', value: inactiveApps, color: 'var(--text-muted)' }
  ]

  const barData = Object.entries(providerCounts).map(([label, value]) => ({
    label: label.charAt(0).toUpperCase() + label.slice(1),
    value
  }))

  return (
    <div className="page-content">
      <VStack gap="xl">
        <Animate type="fadeIn">
          <div>
            <Heading level={2}>Statistiques</Heading>
            <Text color="muted">Vue d'ensemble de vos applications Webba ID</Text>
          </div>
        </Animate>

        <Grid columns={{ xs: 1, sm: 2, lg: 4 }} gap="md">
          <Animate type="slideInUp" delay={50}>
            <StatCard
              label="Utilisateurs totaux"
              value={globalStats?.total_users ?? 0}
              icon={<People20Regular />}
              color="var(--brand-primary)"
            />
          </Animate>
          <Animate type="slideInUp" delay={100}>
            <StatCard
              label="Actifs (7 jours)"
              value={globalStats?.active_users_7d ?? 0}
              icon={<ArrowTrending20Regular />}
              color="var(--success)"
            />
          </Animate>
          <Animate type="slideInUp" delay={150}>
            <StatCard
              label="Actifs (30 jours)"
              value={globalStats?.active_users_30d ?? 0}
              icon={<ArrowTrending20Regular />}
              color="var(--info)"
            />
          </Animate>
          <Animate type="slideInUp" delay={200}>
            <StatCard
              label="Connexions totales"
              value={globalStats?.total_logins ?? 0}
              icon={<ArrowTrending20Regular />}
              color="var(--brand-secondary)"
            />
          </Animate>
        </Grid>

        <Grid columns={{ xs: 1, md: 2 }} gap="lg">
          <Animate type="slideInUp" delay={250}>
            <Card padding="lg">
              <VStack gap="md">
                <Heading level={4}>Statut des applications</Heading>
                {applications.length > 0 ? (
                  <DonutChart
                    data={donutData}
                    size={200}
                    thickness={40}
                    showLegend
                    legendBelow
                    animated
                  />
                ) : (
                  <Text color="muted">Aucune application à afficher</Text>
                )}
              </VStack>
            </Card>
          </Animate>

          <Animate type="slideInUp" delay={300}>
            <Card padding="lg">
              <VStack gap="md">
                <Heading level={4}>Utilisation des fournisseurs OAuth</Heading>
                {barData.length > 0 ? (
                  <BarChart
                    data={barData}
                    height={200}
                    showLabels
                    showValues
                    animated
                    color="var(--brand-primary)"
                  />
                ) : (
                  <Text color="muted">Aucune donnée à afficher</Text>
                )}
              </VStack>
            </Card>
          </Animate>
        </Grid>

        {appStats && appStats.length > 0 && (
          <Animate type="slideInUp" delay={350}>
            <Card padding="lg">
              <VStack gap="md">
                <Heading level={4}>Utilisateurs par application</Heading>
                <BarChart
                  data={appStats.map(app => ({
                    label: app.app_name,
                    value: app.total_users ?? 0
                  }))}
                  height={200}
                  showLabels
                  showValues
                  animated
                  color="var(--brand-primary)"
                />
              </VStack>
            </Card>
          </Animate>
        )}

        <Animate type="slideInUp" delay={400}>
          <Card padding="lg">
            <VStack gap="md">
              <Heading level={4}>Résumé</Heading>
              <Grid columns={{ xs: 2, md: 5 }} gap="md">
                <div style={{ textAlign: 'center' }}>
                  <Heading level={2}>{applications.length}</Heading>
                  <Text size="sm" color="muted">Applications</Text>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Heading level={2}>{activeApps}</Heading>
                  <Text size="sm" color="muted">Actives</Text>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Heading level={2}>{globalStats?.total_users ?? 0}</Heading>
                  <Text size="sm" color="muted">Utilisateurs</Text>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Heading level={2}>{globalStats?.apps_with_users ?? 0}</Heading>
                  <Text size="sm" color="muted">Apps avec utilisateurs</Text>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Heading level={2}>{Object.keys(providerCounts).length}</Heading>
                  <Text size="sm" color="muted">Fournisseurs utilisés</Text>
                </div>
              </Grid>
            </VStack>
          </Card>
        </Animate>
      </VStack>
    </div>
  )
}
